import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { AgentTaskStatus, DigestedTaskStatus, FullDigestedTask } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { IndividualsDuplexerChannel } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';
import { channelListenerCount } from './duplexer-connection';
import { queryClient, queryKeys } from './query-client';
import { noteFetch } from './runaway-fetch-probe';
import { useAgentTaskDuplexer } from './use-agent-task-duplexer';


// On the v1 path this is the inner FullDigestedTask + the digest id. On the v2 (AgentTask) path the
// digest-only fields (meetingDetails / taskType / taskProgress) are absent and `status` may be any of
// the 10 AgentTaskStatus values; `currentAction` / `summary` carry the lean AgentTask display. The
// task's Master Plan is flattened onto `plan` as a bare step array on both paths (v1 from
// FullDigestedTask.plan.steps, v2 parsed from the AgentTask's display.plan) — `[]` when there is none.
export type WebTask = Omit<FullDigestedTask, 'status' | 'plan'> & {
  digestId: string;
  status: DigestedTaskStatus | AgentTaskStatus;
  currentAction?: string;
  summary?: string;
  plan: { title: string; completed: boolean }[];
  /** The Space (workspace) the task belongs to — carried so the cross-workspace Tasks list can show a
   *  per-card Space indicator and filter by Space. Empty on the v1 digest path / unscoped tasks. */
  workspaceId?: string;
  /** The agent the task is assigned to — carried so the per-agent Tasks list (on the agent screen) can
   *  filter to that agent's tasks. Empty on the v1 digest path. */
  agentId?: string;
  /** Files the task produced (VFS paths) — the outcome line on a completed card. Absent on the v1 path. */
  artifacts?: string[];
  /** The display subtitle the agent wrote alongside the title — the other half of that outcome line. */
  subtitle?: string;
  /** The agent's own working conversation, and the reviewer's when the task is in review. Both are carried
   *  because a task in review has its activity on the REVIEWER conversation, so "is it actually running"
   *  cannot be answered from either alone. Distinct from `conversationId`, the chat-handoff target. */
  agentConversationId?: string;
  reviewerConversationId?: string;
  /** Is an agent loop executing on this task right now, and when did it last do anything? Stamped by the
   *  route's conversation join for the unsettled tasks only — absent means "not computed", which
   *  `isInMotion` must never read as "not running". Feeds the island count and the card's live marker. */
  runInFlight?: boolean;
  lastActivityAt?: string;
  /** This task tracks a dashboard BUILD, which runs service-side with no agent conversation behind it
   *  (see the harness's `dashboard-build-task.ts`). The detail drill-in reads it to say so, instead of
   *  promising a transcript that will never arrive. */
  isDashboardBuild?: boolean;
  /** Present only on a row that is NOT an AgentTask — an inbound outreach with no task of its own.
   *  The row renders like any other; this tells the card a click opens the conversation the user's
   *  own agent presented it in, since there is no task detail to show. */
  rowKind?: 'outreach';
  /** Who sent it, for an outreach row's title. */
  senderName?: string;
  /** The whole thread, oldest first. An outreach row opens IN PLACE — the conversation it was
   *  delivered into is not in the user's chat list, so navigating to it lands on the root. */
  outreachMessages?: { text: string; at?: string; relative?: string; mine: boolean; awaitedYou: boolean; resolvedByIndex?: number }[];
  /** Outreaches this task sent — who it reached, and whether they ever answered. */
  sentOutreaches?: { id: string; recipientName?: string; channel?: string; replied: boolean; awaitingReply: boolean; lastMessage?: string; conversationId?: string }[];
};

export type TasksState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; tasks: WebTask[] };

export type TaskBucket = DigestedTaskStatus.ACTION_NEEDED | DigestedTaskStatus.IN_PROGRESS | DigestedTaskStatus.COMPLETED;

export type TasksByBucket = Record<TaskBucket, WebTask[]>;

// Mirrors mobile's categorizeTasksByStatus: PENDING and ACTION_NEEDED fold
// into one user-facing "action needed" pile, PAUSED + IN_PROGRESS + WAITING
// (+ the v2 IN_REVIEW) fold into one "in progress" pile, COMPLETED stays on
// its own. The v2-only terminal CANCELLED joins the completed pile; the v1
// digest status set never produces CANCELLED/IN_REVIEW, so this is a no-op
// there.
export function categorizeTasksByStatus(tasks: WebTask[]): TasksByBucket {
  const buckets: TasksByBucket = {
    [DigestedTaskStatus.ACTION_NEEDED]: [],
    [DigestedTaskStatus.IN_PROGRESS]: [],
    [DigestedTaskStatus.COMPLETED]: [],
  };
  for (const task of tasks) {
    const bucket: TaskBucket =
      task.status === DigestedTaskStatus.COMPLETED || task.status === AgentTaskStatus.CANCELLED
        ? DigestedTaskStatus.COMPLETED
        : task.status === DigestedTaskStatus.ACTION_NEEDED || task.status === DigestedTaskStatus.PENDING
        ? DigestedTaskStatus.ACTION_NEEDED
        : DigestedTaskStatus.IN_PROGRESS;
    buckets[bucket].push(task);
  }
  return buckets;
}

async function fetchTasks(workspaceId?: string): Promise<WebTask[]> {
  // Every mounted useTasks registers an agent-task listener that refetches THIS entry, and one
  // duplexer event runs them all in a single tick — so the listener count is the burst's own
  // multiplier, and the only figure a stack trace cannot supply.
  noteFetch('tasks', workspaceId ?? 'account-wide', () => `subs=${channelListenerCount(IndividualsDuplexerChannel.AGENT_TASK)}`);
  const query = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
  const res = await apiFetch(`${API_BASE}/api/tasks${query}`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  const data = (await res.json()) as { tasks?: WebTask[] };
  return data.tasks ?? [];
}

// Strips response bodies down to one human-readable line. Prevents the
// raw 404 HTML page (or any other non-JSON response) from being dumped
// into the UI as an error message.
async function readErrorMessage(res: Response): Promise<string> {
  const fallback = `Couldn't load tasks (${res.status})`;
  const text = await res.text().catch(() => '');
  if (!text) return fallback;
  if (text.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(text) as { message?: string; error?: string };
      const msg = parsed.message || parsed.error;
      if (msg) return msg;
    } catch {
      // fall through to status-only fallback
    }
  }
  return fallback;
}

/**
 * The Agent Tasks list. `workspaceId` scopes it to that workspace + its child projects (the workspace's
 * "Agents at work" links in); omitted, it's the account-wide list the top-level Tasks nav entry shows.
 *
 * `enabled: false` only pauses fetching — a cached list for that key is still served, so a caller that
 * must show nothing until it knows its scope has to suppress the state itself (see useScopedTasks).
 */
export function useTasks(workspaceId?: string, options?: { enabled?: boolean }): {
  state: TasksState;
  refresh: () => Promise<void>;
} {
  const enabled = options?.enabled ?? true;
  const query = useQuery({
    queryKey: queryKeys.tasks(workspaceId),
    queryFn: () => fetchTasks(workspaceId),
    enabled,
  });

  useAgentTaskDuplexer(() => {
    if (enabled) void query.refetch();
  });

  const refresh = async () => {
    await query.refetch();
  };

  // Keep a prior good list on a transient refetch failure — only surface an error before the first success.
  if (query.data) return { state: { kind: 'ready', tasks: query.data }, refresh };
  if (enabled && query.status === 'error') {
    const message = query.error instanceof Error ? query.error.message : String(query.error);
    return { state: { kind: 'error', message }, refresh };
  }
  return { state: { kind: 'loading' }, refresh };
}

/**
 * Apply `update` to a task across EVERY cached scope (account-wide and each workspace-scoped list), so a
 * mutation fired from one screen can't leave another showing the stale status. Returns the pre-edit task
 * for rollback, or null when it wasn't cached anywhere.
 */
function updateTaskInCache(id: string, update: (task: WebTask) => WebTask): WebTask | null {
  let previous: WebTask | null = null;
  queryClient.setQueriesData<WebTask[]>({ queryKey: queryKeys.tasksAllScopes }, (tasks) =>
    tasks?.map((task) => {
      if (task.id !== id) return task;
      previous = task;
      return update(task);
    }),
  );
  return previous;
}

function replaceTask(id: string, replacement: WebTask): void {
  updateTaskInCache(id, () => replacement);
}

function patchTaskInStore(id: string, patch: Partial<WebTask>): WebTask | null {
  return updateTaskInCache(id, (task) => ({ ...task, ...patch }));
}

// Server response is a FullTaskDigest (the same Digest<FullDigestedTask>
// wrapper /api/tasks unwraps). Reshape back into WebTask before storing.
function digestToWebTask(digest: { id?: string; data?: FullDigestedTask }): WebTask | null {
  if (!digest.data || !digest.id) return null;
  return { ...digest.data, digestId: digest.id, plan: digest.data.plan?.steps ?? [] };
}

async function postMutation(
  taskId: string,
  endpoint: 'done' | 'pause' | 'resume',
  optimisticPatch: Partial<WebTask>,
  label: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  const previous = patchTaskInStore(taskId, optimisticPatch);
  try {
    const res = await apiFetch(`${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/${endpoint}`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) {
      if (previous) replaceTask(taskId, previous);
      return { ok: false, message: await readErrorMessageFor(res, label) };
    }
    const data = (await res.json()) as { task?: { id?: string; data?: FullDigestedTask } };
    const updated = data.task ? digestToWebTask(data.task) : null;
    if (updated) replaceTask(taskId, updated);
    return { ok: true };
  } catch (err: unknown) {
    if (previous) replaceTask(taskId, previous);
    return { ok: false, message: err instanceof Error ? err.message : String(err) };
  }
}

export function markTaskAsDone(taskId: string) {
  return postMutation(
    taskId,
    'done',
    { status: DigestedTaskStatus.COMPLETED },
    "Couldn't mark task as done",
  );
}

export function pauseTask(taskId: string) {
  return postMutation(taskId, 'pause', { status: DigestedTaskStatus.PAUSED }, "Couldn't pause task");
}

export function resumeTask(taskId: string) {
  return postMutation(
    taskId,
    'resume',
    { status: DigestedTaskStatus.IN_PROGRESS },
    "Couldn't resume task",
  );
}

/**
 * The user calls a task off. Not a `postMutation` sibling, and the difference is the response shape:
 * `/api/tasks/:id/cancel` proxies the native-data `task-cancel` action and echoes a bare `AgentTask`,
 * where done/pause/resume echo a `Digest<FullDigestedTask>` — so there is nothing here to reshape and
 * the reconcile is the echoed status alone.
 *
 * Optimistic + rollback, the house style: patch every cached scope to CANCELLED, reconcile from the
 * echo, restore the pre-edit task on any failure. The open detail modal reads a different cache entry
 * (`['task-detail', id]`), so it is patched alongside — without it the list row would read Cancelled
 * while the sheet the user is looking at still said In progress.
 */
export async function cancelTask(taskId: string): Promise<{ ok: true } | { ok: false; message: string }> {
  const previous = patchTaskInStore(taskId, { status: AgentTaskStatus.CANCELLED });
  const previousDetail = patchTaskDetailInCache(taskId, AgentTaskStatus.CANCELLED);
  const rollback = () => {
    if (previous) replaceTask(taskId, previous);
    if (previousDetail !== null) patchTaskDetailInCache(taskId, previousDetail);
  };
  try {
    const res = await apiFetch(`${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/cancel`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) {
      rollback();
      return { ok: false, message: await readErrorMessageFor(res, "Couldn't cancel task") };
    }
    const data = (await res.json()) as { task?: { status?: string } | null };
    const status = data.task?.status;
    if (status) {
      patchTaskInStore(taskId, { status: status as AgentTaskStatus });
      patchTaskDetailInCache(taskId, status);
    }
    return { ok: true };
  } catch (err: unknown) {
    rollback();
    return { ok: false, message: err instanceof Error ? err.message : String(err) };
  }
}

/** Writes `status` onto the single-task detail entry, returning the one it replaced (null if uncached). */
function patchTaskDetailInCache(taskId: string, status: string): string | null {
  let previous: string | null = null;
  queryClient.setQueryData<{ status?: string }>(['task-detail', taskId], (task) => {
    if (!task) return task;
    previous = task.status ?? '';
    return { ...task, status };
  });
  return previous;
}

async function readErrorMessageFor(res: Response, fallbackLabel: string): Promise<string> {
  const fallback = `${fallbackLabel} (${res.status})`;
  const text = await res.text().catch(() => '');
  if (!text) return fallback;
  if (text.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(text) as { message?: string; error?: string };
      const msg = parsed.message || parsed.error;
      if (msg) return msg;
    } catch {
      // fall through
    }
  }
  return fallback;
}
